import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import PageWrapper from "@/components/PageWrapper.tsx";
import BaseButton from "@/components/base/button/BaseButton.tsx";
import FloatingLabelInput from "@/components/form/FloatingLabelInput.tsx";
import showCustomToast from "@/shared/utils/showCustomToast.ts";
import {
  classifyCardError,
  extractErrorMessage,
  isTechnicalError,
  useBindCardConfirm,
  useBindCardInit,
} from "./api.ts";
import { useGetProfile, useSharePhone } from "@/pages/profile/api";
import { Phone, RotateCw } from "lucide-react";
import OtpBoxes from "./OtpBoxes.tsx";

const formatCardNumber = (raw: string) =>
  raw
    .replace(/\D/g, "")
    .slice(0, 16)
    .replace(/(.{4})/g, "$1 ")
    .trim();

const formatExpiry = (raw: string) => {
  const digits = raw.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const isValidExpiry = (e: string) => {
  if (!/^\d{2}\/\d{2}$/.test(e)) return false;
  const month = Number(e.slice(0, 2));
  return month >= 1 && month <= 12;
};

const OTP_LENGTH = 6;

type Step = "card" | "otp";

export default function AddCardPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: profile } = useGetProfile();
  const hasPhone = Boolean(profile?.phone);
  const { sharePhone, isPending: isSharingPhone } = useSharePhone();

  const [step, setStep] = useState<Step>("card");
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cid, setCid] = useState<string | null>(null);
  const [otpSentTo, setOtpSentTo] = useState<string | null>(null);
  const [otp, setOtp] = useState("");
  const [resendCountdown, setResendCountdown] = useState(0);
  // Flipped on bindConfirm failure so the 6 boxes turn red. Cleared on
  // the next keystroke so the error doesn't linger past the correction.
  const [otpError, setOtpError] = useState(false);

  // First-box focus on OTP step is handled by OtpBoxes' `autoFocus`.

  // Last OTP value we actually sent — auto-submit only fires on a fresh
  // value so we don't re-toast the same wrong code.
  const lastSubmittedOtpRef = useRef<string>("");

  const { mutate: bindInit, isPending: isInitPending } = useBindCardInit();
  const { mutate: bindConfirm, isPending: isConfirmPending } = useBindCardConfirm();

  const isCardValid = useMemo(() => {
    const digits = cardNumber.replace(/\D/g, "");
    return digits.length === 16 && isValidExpiry(expiry);
  }, [cardNumber, expiry]);

  const isOtpValid = otp.length >= 4 && otp.length <= OTP_LENGTH;

  // Resend countdown
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const id = setTimeout(() => setResendCountdown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCountdown]);

  const expireDateForApi = (raw: string) => {
    const [mm, yy] = raw.split("/");
    return `${yy}${mm}`;
  };

  const startBinding = () => {
    if (!isCardValid || isInitPending) return;
    bindInit(
      {
        card_number: cardNumber.replace(/\D/g, ""),
        expire_date: expireDateForApi(expiry),
      },
      {
        onSuccess: (data) => {
          setCid(data.cid);
          setOtpSentTo(data.otp_sent_to);
          setResendCountdown(data.resend_after_seconds || 60);
          setOtp("");
          setStep("otp");
        },
        onError: (err) => {
          const kind = classifyCardError(err);
          let message: string;
          if (kind === "RATE_LIMITED") {
            message =
              t("card_rate_limited") ||
              "Bir oz kuting, juda ko'p so'rov yuborildi";
          } else if (kind === "CARD_DUPLICATE") {
            message =
              t("card_duplicate") || "Bu karta sizda allaqachon mavjud";
          } else if (kind === "PHONE_REQUIRED") {
            message =
              t("card_phone_required_title") ||
              "Avval telefon raqamingizni kiriting";
          } else if (kind === "CARD_INVALID") {
            message =
              t("card_invalid") ||
              "Karta ma'lumotlarini tekshiring";
          } else {
            message = extractErrorMessage(
              err,
              t("card_bind_failed") || "Karta qo'shilmadi"
            );
          }
          showCustomToast(message, "error");
        },
      }
    );
  };

  const resendOtp = () => {
    if (resendCountdown > 0 || isInitPending) return;
    startBinding();
  };

  const confirmOtp = () => {
    if (!isOtpValid || !cid || isConfirmPending) return;
    lastSubmittedOtpRef.current = otp;
    bindConfirm(
      { cid, otp },
      {
        onSuccess: () => {
          showCustomToast(t("card_added") || "Karta qo'shildi", "success");
          navigate("/profile/cards", { replace: true });
        },
        onError: (err) => {
          const kind = classifyCardError(err);
          let message: string;
          if (kind === "OTP_LOCKED") {
            message =
              t("otp_locked") ||
              "Juda ko'p noto'g'ri urinish. Birozdan so'ng qayta urinib ko'ring";
          } else if (kind === "RATE_LIMITED") {
            message =
              t("card_rate_limited") ||
              "Bir oz kuting, juda ko'p so'rov yuborildi";
          } else if (isTechnicalError(err)) {
            message = t("card_bind_failed") || "Xatolik yuz berdi";
          } else {
            // Any other confirm failure means the entered code was wrong
            // or expired — never surface the backend's raw gateway message
            // ("Domopay so'rovi noto'g'ri"); always say invalid OTP.
            message = t("invalid_otp") || "Tasdiqlash kodi noto'g'ri";
          }
          showCustomToast(message, "error");
          setOtpError(true);
        },
      }
    );
  };

  // Auto-submit once all 6 digits are typed. Skips when the value
  // matches the last submitted one (prevents duplicate toasts when
  // an effect re-runs without a real input change).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (step !== "otp") return;
    if (otp.length !== OTP_LENGTH) return;
    if (isConfirmPending || otpError) return;
    if (!cid) return;
    if (otp === lastSubmittedOtpRef.current) return;
    confirmOtp();
  }, [otp, step, isConfirmPending, otpError, cid]);

  if (!hasPhone) {
    return (
      <PageWrapper backPath="/profile/cards">
        <div
        className="container pt-safe-top pb-safe-bottom fixed inset-x-0 top-0 flex flex-col"
        style={{ height: "100dvh" }}
      >
          <div className="flex items-center justify-between gap-3 py-3">
            <h1 className="text-lg font-semibold text-white leading-130">
              {t("add_card") || "Karta qo'shish"}
            </h1>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-5">
            <div className="size-20 rounded-full bg-orange-100/15 border border-orange-100/30 flex-center">
              <Phone className="size-10 text-orange-100" strokeWidth={1.5} />
            </div>
            <div className="flex flex-col gap-2 max-w-xs">
              <h2 className="text-xl font-bold text-white leading-130">
                {t("card_phone_required_title") ||
                  "Avval telefon raqamingizni kiriting"}
              </h2>
              <p className="text-sm text-white/70 leading-160">
                {t("card_phone_required_description") ||
                  "Karta qo'shish uchun telefon raqamingizni ulashing."}
              </p>
            </div>
          </div>
          <div className="pt-3">
            <BaseButton
              className="w-full disabled:!bg-white/08 disabled:!text-gray-1"
              variant="premium"
              text={t("add_phone_now") || "Telefon raqamini ulash"}
              onClick={sharePhone}
              isLoading={isSharingPhone}
              vibrate
              vibrationType="soft"
            />
          </div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper backPath={step === "otp" ? undefined : "/profile/cards"}>
      <div
        className="container pt-safe-top pb-safe-bottom fixed inset-x-0 top-0 flex flex-col"
        style={{ height: "100dvh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 py-3">
          <h1 className="text-lg font-semibold text-white leading-130">
            {step === "card"
              ? t("add_card") || "Karta qo'shish"
              : t("otp_verify") || "Tasdiqlash kodi"}
          </h1>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto pt-2 flex flex-col">
          {step === "card" ? (
            <div className="flex flex-col gap-2.5">
              <FloatingLabelInput
                label={t("card_number") || "Karta raqami"}
                placeholder="0000 0000 0000 0000"
                value={cardNumber}
                onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                type="tel"
                inputMode="numeric"
                autoFocus
                maxLength={19}
              />
              <FloatingLabelInput
                label={t("card_expiry_short", { defaultValue: "Muddati" })}
                placeholder="__ / __"
                value={expiry}
                onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                type="tel"
                inputMode="numeric"
                maxLength={5}
                className="max-w-[180px]"
              />
            </div>
          ) : (
            // Mirror of AddCardSheet's OTP step — kept in sync so users get
            // the same OTP UX from sheet or page entry. `flex-1 justify-center`
            // vertically parks the whole stack so the icon doesn't sit
            // awkwardly at the top of the available body height.
            <div className="flex-1 flex flex-col items-center justify-center text-center pb-[20vh]">
              <div className="size-16 rounded-full bg-orange-100/15 border border-orange-100/30 flex-center mb-3">
                <Phone
                  className="size-7 text-orange-100"
                  strokeWidth={1.5}
                />
              </div>

              <h2 className="text-lg font-bold text-white leading-130 mb-1">
                {t("otp_verify") || "Tasdiqlash kodi"}
              </h2>

              <p className="text-sm text-white/70 leading-150 max-w-[280px] mb-3">
                {t("otp_enter_hint") ||
                  "Quyidagi raqamga yuborilgan OTP-kodni kiriting"}
              </p>

              {otpSentTo && (
                <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-white/08 mb-5">
                  <span className="text-sm font-medium text-white tabular-nums">
                    {otpSentTo.startsWith("+")
                      ? otpSentTo
                      : `+${otpSentTo}`}
                  </span>
                </div>
              )}

              <div className="mb-4">
                <OtpBoxes
                  value={otp}
                  onChange={(next) => {
                    if (otpError) setOtpError(false);
                    setOtp(next);
                  }}
                  length={OTP_LENGTH}
                  error={otpError}
                  autoFocus
                  ariaLabel={t("otp_verify") || "Tasdiqlash kodi"}
                />
              </div>

              <button
                type="button"
                onClick={resendOtp}
                disabled={resendCountdown > 0 || isInitPending}
                className={`inline-flex items-center gap-2 text-sm transition-colors disabled:cursor-default ${
                  resendCountdown > 0
                    ? "text-gray-1 font-normal"
                    : "text-blue font-semibold"
                }`}
              >
                <RotateCw
                  className={`size-4 ${isInitPending ? "animate-spin" : ""}`}
                  strokeWidth={2}
                />
                <span>{t("otp_resend") || "Kodni qayta yuborish"}</span>
                {resendCountdown > 0 && (
                  <span className="px-2 py-0.5 rounded-md bg-white/08 text-xs font-medium text-white tabular-nums">
                    {`${Math.floor(resendCountdown / 60)
                      .toString()
                      .padStart(2, "0")}:${(resendCountdown % 60)
                      .toString()
                      .padStart(2, "0")}`}
                  </span>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Bottom action — only on the card step. OTP auto-submits on
            the last digit, so we render nothing there (avoids leaving
            an empty padded area / orphan visual). */}
        {step === "card" && (
          <div
            className="flex flex-col gap-3 pt-3 transition-[padding-bottom] duration-150"
            style={{
              // resizes-content (index.html) lifts this bar above the keypad
              // on keyboard open — no JS keyboardHeight offset (double-counted
              // on Pro Max). env(keyboard-inset-height) stays as a fallback.
              paddingBottom: "env(keyboard-inset-height, 0px)",
            }}
          >
            <BaseButton
              className="w-full disabled:!bg-white/08 disabled:!text-gray-1"
              variant="premium"
              text={t("continue") || "Davom etish"}
              onClick={startBinding}
              disabled={!isCardValid}
              isLoading={isInitPending}
              vibrate
              vibrationType="soft"
            />
          </div>
        )}
      </div>
    </PageWrapper>
  );
}
